import { Button, Column, Icon, Row, Sheet, Text, Select, SelectOption } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import React, { useState } from "react";
import { COLORS } from "src/constants/colors";
import { Result } from "src/data/types";
import { useAppDispatch } from "src/store/store";
import { updateInspectionResult, updateInspectionResults } from "src/store/inspectionResultsSlice";
import { getInspectionById } from "src/store/inspectionSlice";

type PhotoReviewSheetProps = {
  open: boolean;
  onClose: () => void;
  result: Result;
  evidencePresignedUrl?: string;
  inspectionId: string;
  onUpdate?: () => void;
};

const PhotoReviewSheet = ({
  open,
  onClose,
  result,
  evidencePresignedUrl,
  inspectionId,
  onUpdate,
}: PhotoReviewSheetProps) => {
  const dispatch = useAppDispatch();
  const [showOverrideReason, setShowOverrideReason] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use presigned URL if provided, otherwise fall back to S3 URI
  const evidenceUri = evidencePresignedUrl || result.evidence?.[0]?.uri || "";
  const isCompliant = result.isCompliant;
  const isOverridden = result.isInspectorOverride;

  const handleAccept = async () => {
    // If already accepted (not overridden), just close
    if (!isOverridden) {
      onClose();
      return;
    }

    // If was overridden, reset to AI result
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("ai_result", result.aiResult);
      formData.append("confirmed_result", result.aiResult);
      formData.append("is_compliant", String(result.isCompliant));
      formData.append("is_inspector_override", "false");

      await dispatch(
        updateInspectionResult({
          inspection_id: inspectionId,
          question_id: result.questionId,
          formData,
        })
      ).unwrap();

      // Update local Redux state
      dispatch(
        updateInspectionResults({
          ...result,
          isInspectorOverride: false,
          overrideReason: "",
        })
      );

      // Trigger refresh if callback provided
      if (onUpdate) {
        await dispatch(getInspectionById(inspectionId));
        onUpdate();
      }

      onClose();
    } catch (error) {
      console.error("Failed to accept AI result:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverrideClick = () => {
    setShowOverrideReason(true);
  };

  const handleOverrideSubmit = async () => {
    if (!overrideReason) return;

    setIsSubmitting(true);
    try {
      // Flip the compliance result
      const newIsCompliant = !isCompliant;
      const newConfirmedResult = newIsCompliant ? "YES" : "NO";

      const formData = new FormData();
      formData.append("ai_result", result.aiResult);
      formData.append("confirmed_result", newConfirmedResult);
      formData.append("is_compliant", String(newIsCompliant));
      formData.append("is_inspector_override", "true");
      formData.append("override_reason", overrideReason);

      await dispatch(
        updateInspectionResult({
          inspection_id: inspectionId,
          question_id: result.questionId,
          formData,
        })
      ).unwrap();

      // Update local Redux state
      dispatch(
        updateInspectionResults({
          ...result,
          isCompliant: newIsCompliant,
          confirmedResult: newConfirmedResult,
          isInspectorOverride: true,
          overrideReason: overrideReason,
        })
      );

      // Trigger refresh if callback provided
      if (onUpdate) {
        await dispatch(getInspectionById(inspectionId));
        onUpdate();
      }

      onClose();
      setShowOverrideReason(false);
      setOverrideReason("");
    } catch (error) {
      console.error("Failed to override result:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      className="photo-review-sheet"
      type="overlay"
      side="bottom"
      open={open}
      onClose={onClose}
    >
      <div style={{ borderRadius: "21px" }}>
        <Column spacing="400" spacingInset="500">
          {/* Header */}
          <Row alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="h300">Review Result</Text>
            <div
              style={{ cursor: "pointer" }}
              onClick={onClose}
            >
              <Icon tokens={closeSmallTokens} color="primary" />
            </div>
          </Row>

          {/* Photo */}
          {evidenceUri && (
            <img
              src={evidenceUri}
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                borderRadius: "8px",
                backgroundColor: COLORS.DARKER_WHITE,
              }}
              alt="Evidence"
            />
          )}

          {/* AI Result Display */}
          {!showOverrideReason && (
            <Column spacing="200">
              <Row spacing="200" alignmentVertical="center">
                <Text type="b200">AI Result:</Text>
                <Text
                  type="b300"
                  color={isCompliant ? "success" : "error"}
                >
                  {isCompliant ? "Compliant" : "Non-Compliant"}
                </Text>
              </Row>

              {result.aiReasoning && (
                <Column spacing="100">
                  <Text type="b200">AI Reasoning:</Text>
                  <Text type="b100" color="secondary">
                    {result.aiReasoning}
                  </Text>
                </Column>
              )}

              {isOverridden && (
                <Row
                  spacingInset="100 200"
                  backgroundColor={COLORS.AI_RESULT_OVERRIDE_COLOR}
                >
                  <Text type="b100" color="primary">
                    Inspector Override
                  </Text>
                </Row>
              )}
            </Column>
          )}

          {/* Override Reason Selector */}
          {showOverrideReason && (
            <Column spacing="300">
              <Text type="b200">Select Override Reason:</Text>
              <Select
                value={overrideReason}
                onChange={(value) => setOverrideReason(value as string)}
                placeholder="Select a reason"
              >
                <SelectOption value="false_positive" label="AI False Positive" />
                <SelectOption value="false_negative" label="AI False Negative" />
                <SelectOption value="context_needed" label="Additional Context Needed" />
                <SelectOption value="partial_compliance" label="Partial Compliance" />
                <SelectOption value="other" label="Other" />
              </Select>
            </Column>
          )}

          {/* Action Buttons */}
          <Row spacing="300" widths="fill">
            {showOverrideReason ? (
              <>
                <Button
                  type="tertiary"
                  size="large"
                  onClick={() => {
                    setShowOverrideReason(false);
                    setOverrideReason("");
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  onClick={handleOverrideSubmit}
                  disabled={!overrideReason || isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Confirm Override"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="tertiary"
                  size="large"
                  onClick={handleOverrideClick}
                  disabled={isSubmitting}
                >
                  Override
                </Button>
                <Button
                  size="large"
                  onClick={handleAccept}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Accept AI Answer"}
                </Button>
              </>
            )}
          </Row>
        </Column>
      </div>
    </Sheet>
  );
};

export default PhotoReviewSheet;
